// apps/spa/src/hooks/useSessionExpired.ts
// Subscribes to the 'session-expired' DOM CustomEvent that lib/api.ts dispatches
// on 401 / SESSION_EXPIRED. Returns:
//   - open: boolean — whether the modal is showing
//   - dismiss: () => void — close (used only for testing; the production modal
//     is non-dismissable per CONTEXT 401 contract)
//   - loginAgain: () => void — hard-redirect to /api/auth/start?returnTo=<path>
//
// The form-state preservation step (writing in-flight reminder draft to
// sessionStorage before redirecting) is added by 03-03's reminder editor — it
// will write to sessionStorage BEFORE this redirect runs. 03-01 ships the
// redirect mechanism only.

import { useCallback, useEffect, useState } from 'react';

const FORM_STATE_PREFIX = 'guardian:form-state:';

export function useSessionExpired(): {
  open: boolean;
  dismiss: () => void;
  loginAgain: () => void;
} {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handler = () => setOpen(true);
    document.addEventListener('session-expired', handler);
    return () => document.removeEventListener('session-expired', handler);
  }, []);

  const dismiss = useCallback(() => setOpen(false), []);

  const loginAgain = useCallback(() => {
    const returnTo = window.location.pathname + window.location.search;
    window.location.href = `/api/auth/start?returnTo=${encodeURIComponent(returnTo)}`;
  }, []);

  return { open, dismiss, loginAgain };
}

// Exposed so 03-03's reminder editor can use the same key prefix when writing
// in-flight form state to sessionStorage before the redirect.
export const _FORM_STATE_PREFIX = FORM_STATE_PREFIX;
